import React, { useEffect, useState } from 'react';
import { Wand2, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

import Modal from '@/components/ui/Modal';
import { supabase } from '@/lib/supabase';
import { StoryTemplate, StoryContent, StorySticker, StickerStyle } from '@/types/media';

const STICKER_STYLE_VALUES: StickerStyle[] = ['pill', 'badge', 'promo', 'link'];

interface AiGenerateModalProps {
    isOpen: boolean;
    onClose: () => void;
    templates: StoryTemplate[];
    /** Template preselezionato (es. quello della categoria filtrata). */
    defaultTemplate?: StoryTemplate | null;
    /** Contesto dati del salone passato all'edge function salon-assistant. */
    context: any;
    /** Chiamata quando l'AI ha generato i contenuti: apre l'editor precompilato. */
    onGenerated: (template: StoryTemplate, content: StoryContent) => void;
}

/** Estrae il primo blocco JSON valido dal testo di risposta del modello. */
const parseStoryJson = (text: string): { titolo?: string; sottotitolo?: string; etichette?: { testo?: string; stile?: string }[] } | null => {
    const start = text.indexOf('{');
    const end = text.lastIndexOf('}');
    if (start === -1 || end === -1 || end <= start) return null;
    try {
        return JSON.parse(text.slice(start, end + 1));
    } catch {
        return null;
    }
};

/**
 * Conferma e configurazione della generazione AI di una storia:
 * l'utente sceglie il template e (opzionale) il tema; l'AI riceve template,
 * etichette esistenti e dati del salone e restituisce testi coerenti.
 */
const AiGenerateModal: React.FC<AiGenerateModalProps> = ({ isOpen, onClose, templates, defaultTemplate = null, context, onGenerated }) => {
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [theme, setTheme] = useState('');
    const [loading, setLoading] = useState(false);

    // Reset a ogni apertura, preselezionando il template suggerito.
    useEffect(() => {
        if (isOpen) {
            setSelectedId(defaultTemplate?.id ?? templates[0]?.id ?? null);
            setTheme('');
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const selected = templates.find((t) => t.id === selectedId) || null;

    const generate = async () => {
        if (!selected || loading) return;
        setLoading(true);
        const query =
            `Genera i contenuti per una storia Instagram del salone.\n` +
            `Template: "${selected.title}" (categoria ${selected.category}).\n` +
            `Testi attuali del template: titolo "${selected.defaultText.title}", sottotitolo "${selected.defaultText.subtitle}", etichetta "${selected.defaultText.cta}".\n` +
            (theme.trim() ? `Tema/occasione richiesta: ${theme.trim()}.\n` : '') +
            `Usa i dati del salone nel contesto (nome, servizi, punti di forza) per rendere i testi attinenti e concreti.\n` +
            `Rispondi SOLO con un JSON valido, senza testo prima o dopo, in questo formato:\n` +
            `{"titolo": "max 5 parole, maiuscolo, d'impatto", "sottotitolo": "max 12 parole", "etichette": [{"testo": "max 4 parole", "stile": "pill|badge|promo|link"}]}\n` +
            `Da 1 a 3 etichette, coerenti col template e con gli stili indicati.`;
        try {
            const { data, error } = await supabase.functions.invoke('salon-assistant', {
                body: { query, context },
            });
            if (error) throw error;
            const parsed = parseStoryJson(String(data?.text || ''));
            if (!parsed?.titolo) throw new Error('Risposta AI non valida');
            const stickers: StorySticker[] = (Array.isArray(parsed.etichette) ? parsed.etichette : [])
                .filter((e) => e?.testo)
                .slice(0, 3)
                .map((e, i) => ({
                    id: `ai-${Date.now()}-${i}`,
                    text: String(e.testo),
                    style: STICKER_STYLE_VALUES.includes(e.stile as StickerStyle) ? (e.stile as StickerStyle) : 'pill',
                }));
            onGenerated(selected, {
                title: String(parsed.titolo),
                subtitle: String(parsed.sottotitolo || ''),
                stickers: stickers.length > 0 ? stickers : [{ id: `ai-${Date.now()}`, text: selected.defaultText.cta, style: 'link' }],
            });
            onClose();
        } catch (_e) {
            toast.error("Errore nella generazione. Riprova tra poco.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            className="max-w-xl"
            headerClassName="bg-white border-b border-gray-100"
            headerContent={
                <div className="flex items-center gap-3 pr-8">
                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-clip-400 to-clip-600 text-white flex items-center justify-center">
                        <Wand2 size={20} />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Genera con AI</h2>
                        <p className="text-sm text-gray-400">L'AI crea i testi della storia partendo dal template e dai dati del tuo salone.</p>
                    </div>
                </div>
            }
        >
            <div className="space-y-5">
                {/* Scelta template */}
                <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Template</label>
                    <div className="grid grid-cols-4 gap-2">
                        {templates.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setSelectedId(t.id)}
                                className={`relative aspect-[9/16] rounded-xl overflow-hidden border-2 transition-all ${selectedId === t.id ? 'border-clip-500 ring-2 ring-clip-200' : 'border-transparent hover:border-gray-200'}`}
                            >
                                <img src={t.bgImage} alt={t.title} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-black/30" />
                                <span className="absolute inset-x-1 bottom-1 text-white text-[9px] font-bold leading-tight drop-shadow">{t.title}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tema (opzionale) */}
                <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Tema o occasione (opzionale)</label>
                    <input
                        type="text"
                        value={theme}
                        onChange={(e) => setTheme(e.target.value)}
                        placeholder="es. promo di luglio, -20% sul colore"
                        className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none font-medium transition-all placeholder:text-gray-400 text-gray-900"
                    />
                </div>

                <div className="rounded-2xl bg-clip-50/60 border border-clip-100 p-3.5 flex items-start gap-2.5">
                    <Sparkles size={15} className="text-clip-500 mt-0.5 shrink-0" />
                    <p className="text-xs text-gray-600 leading-relaxed">
                        L'AI userà il template scelto, le sue etichette e i dati del salone per proporre titolo, sottotitolo ed etichette.
                        Potrai comunque modificare tutto nell'editor prima di scaricare.
                    </p>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 font-bold text-sm transition-colors"
                    >
                        Annulla
                    </button>
                    <button
                        type="button"
                        onClick={generate}
                        disabled={!selected || loading}
                        className="flex items-center gap-2 px-5 py-2.5 bg-clip-500 hover:bg-clip-600 text-white font-bold rounded-xl text-sm transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100"
                    >
                        {loading ? <><Loader2 size={16} className="animate-spin" /> Genero…</> : <><Wand2 size={16} /> Genera</>}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default AiGenerateModal;
